import { LegLocation } from '@api/Trinium/Endpoints/Leg';
import { useAddLocationMutation, useLazyFetchLocationsQuery } from '@api/Trinium/Endpoints/Location/slice';
import LocationButtonContent from '@forms/Appointment/LocationButtonContent';
import LocationListContent from '@forms/Appointment/LocationListContent';
import { FormikComplexAutocomplete } from '@forms/Components/AutocompleteInput';
import { FormikButtonDropdown } from '@forms/Components/Dropdowns/ButtonDropdown';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import useDebouncer from '@hooks/useDebounce';
import useEditLocation from '@hooks/useEditLocation';
import { Field, useFormikContext } from 'formik';
import { useCallback, useEffect, useId, useState } from 'react';
import classNames from 'classnames/bind';
import { Autocomplete, IconButton, ListItemIcon, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/AddCircleOutlineTwoTone';
import EditTwoTone from '@mui/icons-material/EditTwoTone';
import { useAppDispatch } from '@reduxStore';
import { queueModal } from '@components/Modals/modalSlice';
import { formatCityState } from '@/Infrastructure/Utilities/string.formatter';
import { LocationListItem, mapLocationToLegLoc } from '@api/Trinium/Endpoints/Location/types';
import { removeUndefinedProperties } from '@/Infrastructure/Utilities/object.type';

import styles from './Location.module.css';

const styleContext = classNames.bind(styles);

interface LocationFieldProps {
  fieldName: string;
  className?: string;
  disabled?: boolean;
  selectionOnly?: boolean;
  label?: string;
  required?: boolean;
  canAddLocation?: boolean;
  /** Optional override for location lookups. Defaults to the globally selected terminal. */
  terminalNumber?: string;
  /** Additional functionality when a value is selected */
  onChangeExtension?: (value?: unknown) => void;
  /** When editing the location, also update the legs on this job that use it with the saved name and address */
  updateLegsForJobId?: number;
}

export default function LocationField({ fieldName, ...props }: LocationFieldProps) {
  const { getFieldMeta, setFieldValue } = useFormikContext();
  const { value } = getFieldMeta<LegLocation>(fieldName);
  const uniqueFixedCacheKey = useId();
  const editLocation = useEditLocation({
    updateLegsForJobId: props.updateLegsForJobId
  });

  const [, { data: newLocation }] = useAddLocationMutation({
    fixedCacheKey: uniqueFixedCacheKey
  });

  /** If the User added a new location, populate it in the dropdown */
  useEffect(() => {
    if (newLocation) {
      void setFieldValue(fieldName, mapLocationToLegLoc(newLocation), true);
    }
  }, [newLocation, setFieldValue, fieldName]);

  return (
    <Field
      name={fieldName}
      className={props.className}
      /** Callers that leave this off keep the original label-less button; ButtonDropdown only
       * renders the label block when it is given one. */
      label={props.label}
      required={props.required}
      component={FormikButtonDropdown({
        buttonConfig: {
          height: '32px'
        },
        ButtonContent: LocationButtonContent,
        buttonContentProps: {
          location: value,
          emptyValueText: 'No Location Assigned'
        },
        PopupContent: LocationListContent,
        popupContentProps: {
          legLocation: value,
          selectionOnly: props.selectionOnly,
          fixedCacheKey: uniqueFixedCacheKey,
          onEditLocation: editLocation
        }
      })}
      clearable
      disabled={props.disabled}
    />
  );
}

const getLocationOptionKey = (option?: LegLocation) => option?.id ?? null;

const getLocationOptionLabel = (option?: LegLocation) => (option ? `${option.id || 'Unmapped'} - ${option.name ?? 'N/A'}` : '');

const isLocationOptionEqual = (option?: LegLocation, value?: LegLocation) => option?.id === value?.id || option?.name === value?.name;

const RenderLocationOptions = (
  props: React.HTMLAttributes<HTMLLIElement> & {
    key: string;
  },
  value: LegLocation
) => {
  const appDispatch = useAppDispatch();
  const locationSecondary = [value.addr1, formatCityState(value.city, value.state), value.zip]
    .filter((part): part is string => Boolean(part && part.trim().length > 0))
    .join(' ');

  return (
    <li
      {...props}
      key={value.id}
      className={`${props.className ?? ''} ${styles['location-autocomplete-list-item']}`}>
      <span
        className={styleContext({
          bold: true,
          ellipsis: true,
          name: true
        })}
        key={`${value.id}-name`}>
        {`${value.id || 'Unmapped'} - ${value.name ?? 'N/A'}`}
      </span>
      <span
        className={styleContext({
          ['secondary-text']: true,
          ellipsis: true,
          addr1: true
        })}
        key={`${value.id}-addr1`}>
        {locationSecondary}
      </span>
      <ListItemIcon
        key={`location-icon-${value.id}`}
        title='Edit Location'
        className={styles['list-item-ico']}
        onClick={(event) => {
          event.stopPropagation();
          event.preventDefault();
          appDispatch(
            queueModal({
              Component: 'consigneeModal',
              Props: { id: value.id }
            })
          );
        }}>
        <EditTwoTone
          key={`location-edit-${value.id}`}
          fontSize='small'
          titleAccess='Edit Location Details'
          className={styles['edit-location-ico']}
        />
      </ListItemIcon>
    </li>
  );
};

export function LocationAutocompleteField({ fieldName, ...props }: LocationFieldProps) {
  const { setFieldValue, getFieldMeta } = useFormikContext();
  const { value } = getFieldMeta<LegLocation | null>(fieldName);
  const { number: globalTerminalNumber } = useCurrentTerminal();
  const terminalNumber = props.terminalNumber ?? globalTerminalNumber;
  const [fetchLocations, { data: locations, isFetching }] = useLazyFetchLocationsQuery();
  const appDispatch = useAppDispatch();

  const uniqueCacheKey = useId();
  const [, { data: newLocation }] = useAddLocationMutation({
    fixedCacheKey: uniqueCacheKey
  });

  const [debouncedValue, setDebouncedValue] = useState<string | null>(null);

  /** If the User added a new location, populate it in the dropdown */
  useEffect(() => {
    if (newLocation) {
      void setFieldValue(fieldName, mapLocationToLegLoc(newLocation), true);
    }
  }, [newLocation, setFieldValue, fieldName]);

  useDebouncer(
    () => {
      if (debouncedValue && debouncedValue.length > 2) {
        void fetchLocations({
          terminalNumber,
          startsWith: debouncedValue
        });
      }
    },
    [debouncedValue, terminalNumber],
    500
  );

  /**
   * Callback for the on input change event upon keystroke
   */
  const onKeyDown = useCallback((value: string | null) => {
    setDebouncedValue(value);
  }, []);

  const onLocationAdd = () => {
    appDispatch(
      queueModal({
        Component: 'consigneeModal',
        Props: {
          apiFixedCacheKey: uniqueCacheKey,
          defaultValues: value
            ? {
                id: value.id,
                name: value.name,
                address1: value.addr1,
                city: removeUndefinedProperties({
                  id: value.cityId,
                  name: value.city,
                  state: value.state
                }),
                zip: value.zip,
                country: removeUndefinedProperties({
                  id: value.cntryId,
                  name: value.cntryName
                })
              }
            : undefined
        }
      })
    );
  };

  return (
    <div
      className={styleContext({
        ['flex-container']: true,
        [props.className ?? '']: Boolean(props.className)
      })}>
      <Field
        name={fieldName}
        label={props.label ?? 'Location'}
        component={FormikComplexAutocomplete}
        autoCompleteComplexItems={locations ?? []}
        className={styleContext({
          ['location-autocomplete']: true
        })}
        onInputChange={onKeyDown}
        renderOption={RenderLocationOptions}
        getOptionKey={getLocationOptionKey}
        getOptionLabel={getLocationOptionLabel}
        isOptionEqualToValue={isLocationOptionEqual}
        popperClass={styles['location-autocomplete-popper']}
        disabled={props.disabled}
        isLoading={isFetching}
        onChangeExtension={props.onChangeExtension}
      />
      {props.canAddLocation && !props.disabled && (
        <IconButton
          onClick={onLocationAdd}
          className={styles['add-ico']}>
          <AddIcon color='success' />
        </IconButton>
      )}
    </div>
  );
}
export function LocationMultiSelectField({ ...props }: LocationFieldProps) {
  const { number: globalTerminalNumber } = useCurrentTerminal();
  const terminalNumber = props.terminalNumber ?? globalTerminalNumber;
  const [fetchLocations] = useLazyFetchLocationsQuery();
  const { setFieldValue, getFieldMeta } = useFormikContext();
  const { value } = getFieldMeta<LocationListItem[]>(props.fieldName);
  const [options, setOptions] = useState<LocationListItem[]>([]);
  const [loading, setLoading] = useState(false);

  const handleSearch = async (searchValue: string) => {
    if (!searchValue) {
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      const onServiceSearch = async (searchValue: string): Promise<LocationListItem[]> => {
        const result = await fetchLocations({
          terminalNumber,
          startsWith: searchValue
        });
        return result.data ?? [];
      };

      const locations = await onServiceSearch(searchValue);
      setOptions(locations);
    } catch (error) {
      console.error('Error searching services:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Field name={props.fieldName}>
      {() => (
        <Autocomplete
          multiple
          options={options.filter((option) => !value.some((selected) => selected.id === option.id))}
          value={value}
          onChange={(_, newValue) => {
            void setFieldValue(props.fieldName, newValue);
          }}
          onInputChange={(_, newInputValue) => {
            if (newInputValue.length >= 3) {
              void handleSearch(newInputValue);
            } else {
              setOptions([]);
            }
          }}
          getOptionLabel={getServiceItemText}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          loading={loading}
          disabled={props.disabled}
          disableCloseOnSelect
          className={props.className}
          fullWidth
          renderOption={(props, option) => (
            <li
              {...props}
              key={option.id}>
              <div>
                <div style={{ fontWeight: 'bold' }}>
                  {option.name} ({option.id})
                </div>
                <div>
                  {option.addr1} {option.city}, {option.state} {option.zip}
                </div>
              </div>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant='standard'
              label={props.label}
              multiline
              fullWidth
            />
          )}
        />
      )}
    </Field>
  );
}

/**
 * Callback for the Button Popup Content to display the service as a list item
 */
const getServiceItemText = (location: LocationListItem) => `${location.name} `;
